import type { WatchSource } from 'vue'
import type { Page } from '~~/content.config'
import { buildHiddenSet, pruneHiddenNodes } from '#shared/navHidden'
import { persistentCache } from '~/utils/asyncDataCache'
import { slugToCollection } from '~/utils/siteLocale'
import { siteFromUrl } from '~/utils/routeMapping'
import { decodeEntities, decodeNavTree } from '~/utils/text'
import { getAuthRules } from '~/composables/useNavConfig'
import { useArchives } from '~/composables/useArchives'
import { detectNavMode, type SiteNavMode, type SiteNavResult } from '~/utils/navTree'

// Collection-level nav tree cache: queryCollectionNavigation loads ALL locales in one
// collection (e.g. all 9 locale slugs for the 'build' canonical). Dedup at the collection
// level so concurrent site+locale renders share one DB query instead of one per locale.
// The cache is intentionally permanent (no delete-on-resolve): during prerendering the
// content is static, and large collections (400+ pages) must not be re-queried per locale.
// In dev mode, HMR reloads the module, which clears the map.
const _navTreeCache = new Map<string, Promise<any[]>>()

// Runtime budget for a single content query on the Cloudflare Worker. @nuxt/content
// queries D1; a query that walks a large collection can exceed the request's
// CPU/time budget and hang — surfacing as a 524 on server-rendered pages. Any
// SSR path (a protected route, or a public route that somehow missed the
// prerender set and fell to the Worker) runs these, so every unbounded query is
// a latent 524.
const WORKER_QUERY_BUDGET_MS = 6000

/**
 * Bound a runtime content query on the Worker: if it doesn't settle within the
 * budget it resolves to `fallback` (degraded output — empty nav / no row —
 * never a hung request). Prerendering runs in Node against local SQLite and is
 * left UNBOUNDED so baked output is always complete. The underlying query keeps
 * running after a timeout (it can't be cancelled); if you cache the returned
 * promise, attach `.catch` to the ORIGINAL `query` (not the raced result) so a
 * late rejection evicts the cache and never goes unhandled.
 */
function boundOnWorker<T>(query: Promise<T>, fallback: T, ms = WORKER_QUERY_BUDGET_MS): Promise<T> {
  if (import.meta.prerender) return query
  return Promise.race([
    query,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
  ])
}

// Paths whose auth gate is OFF because the BFF is disabled — the server 404s
// them (`hiddenProtectedRoutes`), so they must not render as nav links. Empty
// when the BFF is enabled (muting owns them then), so pruning is a no-op and
// BFF-on builds are unaffected. Sourced from public runtimeConfig so it resolves
// identically at prerender (baked HTML) and on client SPA navigation.
const _EMPTY_HIDDEN: Set<string> = new Set()
let _hiddenNavSet: Set<string> | undefined
// `hiddenNavRoutes` is a build-time constant (same public runtimeConfig for
// every request), so resolve it once. The nav builders below read it AFTER
// awaits inside useAsyncData handlers, where the Nuxt instance context is gone
// during prerender — calling useRuntimeConfig() there throws "instance
// unavailable". primeHiddenNavSet() must run from a composable's synchronous
// setup (instance in scope) before those handlers await; getHiddenNavSet()
// then just reads the memo and never touches the instance.
function primeHiddenNavSet(): void {
  if (_hiddenNavSet) return
  _hiddenNavSet = buildHiddenSet(useRuntimeConfig().public.hiddenNavRoutes as string[] | undefined)
}
function getHiddenNavSet(): Set<string> {
  return _hiddenNavSet ?? _EMPTY_HIDDEN
}

export async function getCollectionNavTree(col: string): Promise<any[]> {
  const cached = _navTreeCache.get(col)
  if (cached) return cached
  const query = queryNav(col, ['icon'])
  // `queryCollectionNavigation` walks the whole collection (all 9 locales) — the
  // heaviest content query and the original 524 culprit on server-rendered docs
  // pages (reload / direct URL; SPA nav renders client-side and is fine). Bound
  // it so the render always completes; an empty tree degrades to no sidebar.
  // Evict on rejection (attached to the original query so a post-timeout
  // rejection still clears the poisoned cache and never goes unhandled).
  query.catch(() => { _navTreeCache.delete(col) })
  const promise = boundOnWorker(query, [] as any[])
  _navTreeCache.set(col, promise)
  return promise
}

// Permanent per-site+locale nav result cache. Same rationale as _navTreeCache:
// content is static during prerendering so results never need invalidation.
const _navResultCache = new Map<string, Promise<SiteNavResult>>()

/**
 * Shared composable for page content — single useAsyncData definition eliminates
 * "incompatible handler" warnings when layout and page both need the same data.
 *
 * Accepts either a URL path (/{locale}/docs/{site}/...) or a content path
 * (/{locale}/{site}/...) — urlToContentPath is idempotent on content paths.
 */
export function usePageContent(routePath: string | Ref<string>) {
  // Capture the protected body synchronously — see `getProtectedBody` for why.
  const protectedBody = getProtectedBody();
  return useAsyncData(
    () => "page-" + toValue(routePath),
    async () => {
      const contentPath = toValue(routePath);
      const site = siteFromUrl(contentPath);
      const col = await slugToCollection(site || "platform");
      // The `col` collection (a docs site) uses `pageSchema`, but it's resolved
      // at runtime — Nuxt-Content's typed-query inference can't see that. The
      // single boundary cast tightens the row to `Page & { body?: unknown }`
      // so callers see typed frontmatter (`redirect`, `layout`, `seo_*`, etc.).
      // `col` is null when `site` isn't a known docs site — skip the per-site
      // lookup and fall through to the `sections` resolution below.
      // Bounded on the Worker: even a path-indexed `.first()` can hang on a
      // cold D1 (e.g. right after a reseed), and this runs on every SSR/
      // Worker-fallback render — an unbounded one 524s. Timeout → null →
      // treated as not-found (fast) rather than a hung request. Prerender
      // stays unbounded so baked pages always resolve their row.
      const row = col
        ? ((await boundOnWorker(
            (await queryPages(col)).path(contentPath).first(),
            null,
          )) as (Page & { body?: unknown; layout?: string }) | null)
        : null;
      if (!row) {
        // Top-level docs leaves (e.g. `nl/5.docs/4.platform.md`) sit directly
        // under the docs root rather than inside a `<N>.<site>/` dir, so they
        // never enter a per-canonical site collection — only the unified
        // `sections` collection picks them up. These files are stubs whose
        // purpose is to forward to the canonical deep path; honour that by
        // returning the sections row's redirect when present. Non-redirect
        // top-level leaves still resolve to `null` — sections rows don't
        // carry the body shape `ContentRenderer` expects, so we can't safely
        // render them through this composable.
        const sectionRedirect = (await boundOnWorker(
          (await queryPages("sections"))
            .path(contentPath)
            .select("redirect")
            .first(),
          null,
        )) as { redirect?: string } | null;
        if (sectionRedirect?.redirect) {
          return { redirect: sectionRedirect.redirect } as Page & {
            body?: unknown;
            layout?: string;
          };
        }
        return null;
      }
      if (protectedBody !== null) {
        return { ...row, body: protectedBody } as Page & { body?: unknown; layout?: string };
      }
      // SPA-nav path: the document request didn't hit the Worker, so the
      // server middleware never set `event.context.protectedContent`. If
      // this route is in `authRules` and the row's body is missing
      // (partition cleared it pre-deploy), fetch the body from the BFF
      // endpoint that mirrors the middleware's R2 read with the same auth gate.
      // `fetchProtectedBodyClient` dedupes the request across the multiple
      // `usePageContent` invocations a single docs route triggers (page,
      // layout, breadcrumb).
      if (import.meta.client && !row.body) {
        // Lazy-fetched from the static `/auth-rules.json` asset — the map is
        // no longer in `runtimeConfig.public` (see `useNavConfig`).
        const rules = await getAuthRules();
        if (rules[contentPath] !== undefined && rules[contentPath] !== false) {
          const body = await fetchProtectedBodyClient(contentPath);
          if (body != null) {
            return { ...row, body } as Page & { body?: unknown; layout?: string };
          }
        }
      }
      return row;
    },
    {
      // Custom: skip cached entries with empty body — see M3 partition splice
      getCachedData(key, nuxtApp) {
        const cached = nuxtApp.payload.data[key] ?? nuxtApp.static?.data?.[key];
        if (cached != null && !cached.body) return undefined;
        return cached ?? undefined;
      },
      // Refetch when the route path changes. The docs page + layout that
      // consume this are reused (not remounted) across SPA navigation, and a
      // reactive key only namespaces the cache — without `watch` the handler
      // never re-runs, so the URL updates but the content stays on the previous
      // page until a hard reload. Matches the [...slug]/useSettings pattern.
      watch: [() => toValue(routePath)],
    },
  );
}

// Shared fetch logic — deduped via _navInflight map.
async function _fetchNavData(site: string, locale: string): Promise<SiteNavResult> {
  const key = `${site}:${locale}`
  const cached = _navResultCache.get(key)
  if (cached) return cached

  const promise = (async () => {
    const col = await slugToCollection(site)
    // Unknown site slug → no collection → empty nav (no sidebar) rather than a
    // phantom-collection query that 404s the content endpoint.
    if (!col) return { mode: 'flat', items: [] } as SiteNavResult
    const fullNav = await getCollectionNavTree(col)
    const localeRoot = fullNav.find((n: any) => n.path === `/${locale}`)
    const docsNode = localeRoot?.children?.find((n: any) => n.path === `/${locale}/docs`)
    const sitePath = `/${locale}/docs/${site}`
    const siteNode = docsNode?.children?.find((n: any) => n.path === sitePath)
    const siteChildren = pruneHiddenNodes(
      siteNode?.children?.filter((c: any) => c.path !== sitePath) ?? [],
      getHiddenNavSet(),
    )
    const mode = detectNavMode(siteChildren)

    // Collect all childless category paths, then fetch only those in a single IN query.
    // Avoids N+1 per-path queries and avoids loading the full collection into memory.
    const childlessPaths = mode === 'flat'
      ? siteChildren
          .filter((cat: any) => !cat.children?.filter((c: any) => c.path !== cat.path).length)
          .map((cat: any) => cat.path)
      : siteChildren.flatMap((ss: any) =>
          (ss.children?.filter((c: any) => c.path !== ss.path) ?? [])
            .filter((cat: any) => !cat.children?.filter((c: any) => c.path !== cat.path).length)
            .map((cat: any) => cat.path),
        )

    const pageSet = new Set<string>()
    if (childlessPaths.length > 0) {
      // Bounded on the Worker: a slow `IN (…)` scan degrades to "no category is
      // a direct page" (childless categories just won't be linked) instead of
      // hanging the SSR render. Prerender stays unbounded.
      const pages = await boundOnWorker(
        (await queryPages(col))
          .where('path', 'IN', childlessPaths)
          .select('path')
          .all(),
        [] as Array<{ path: string }>,
      )
      for (const p of pages) {
        pageSet.add(p.path)
      }
    }

    if (mode === 'flat') {
      const items = siteChildren.map((cat: any) => ({ ...cat, isPage: pageSet.has(cat.path) }))
      decodeNavTree(items)
      return { mode, items }
    } else {
      // Re-attach annotated categories to each sub-site; strip the sub-site's own index node
      const items = siteChildren.map((ss: any) => ({
        ...ss,
        children: (ss.children?.filter((c: any) => c.path !== ss.path) ?? []).map(
          (cat: any) => ({ ...cat, isPage: pageSet.has(cat.path) }),
        ),
      }))
      decodeNavTree(items)
      return { mode, items }
    }
  })()

  // Evict on rejection — see getCollectionNavTree. A failed SPA-time nav fetch
  // must be retryable, not permanently cached as a rejected promise.
  promise.catch(() => { _navResultCache.delete(key) })
  _navResultCache.set(key, promise)
  return promise
}

/**
 * Warms the navigation cache for a given site+locale pair without blocking.
 * No-ops if already cached or inflight. Call this when you know which sites
 * a user might navigate to next (e.g., when the docs section is visible).
 */
export function prefetchSiteNavigation(site: string, locale: string): void {
  const nuxtApp = useNuxtApp()
  const cacheKey = `navigation-${site}-${locale}`
  if (nuxtApp.payload.data[cacheKey] != null) return
  _fetchNavData(site, locale).then(
    (result) => { nuxtApp.payload.data[cacheKey] = result },
    () => {}, // ignore — fetch will retry when NavigationBook mounts
  )
}

/**
 * Synchronously resolves the first leaf page for a site from the prefetched
 * navigation cache. Returns null if the cache isn't populated yet.
 * Used by nav UI (header dropdown, mobile drawer) to point parent links at a
 * concrete leaf so clicks don't land on an empty directory-index page.
 */
export function resolveFirstPage(site: string, locale: string): string | null {
  const nuxtApp = useNuxtApp()
  const navData = nuxtApp.payload.data[`navigation-${site}-${locale}`] as SiteNavResult | undefined
  if (!navData) return null

  function firstLeaf(node: any): string | null {
    const children = node?.children?.filter((c: any) => c.path !== node.path) ?? []
    if (!children.length) return node?.path ?? null
    return firstLeaf(children[0])
  }

  if (navData.mode === 'flat') {
    return firstLeaf(navData.items[0])
  }
  // subsite: first subsite → first category → first page
  const firstSub = navData.items[0]
  const firstCat = firstSub?.children?.[0]
  return firstLeaf(firstCat) ?? firstLeaf(firstSub)
}

/**
 * Shared composable for site navigation tree.
 *
 * Returns { mode, items } where:
 *   - flat mode:    items = categories (with isPage annotation)
 *   - subsite mode: items = sub-site nodes (each .children = annotated categories)
 *
 * Mode is auto-detected from nav tree depth — no config needed.
 * getCachedData prevents re-fetches across SPA navigation within the same locale+site pair.
 */
export function useSiteNavigation(
  site: string | Ref<string>,
  locale: string | Ref<string>,
) {
  primeHiddenNavSet()
  const watchSources: WatchSource[] = []
  if (isRef(site)) watchSources.push(site)
  if (isRef(locale)) watchSources.push(locale)

  const result = useAsyncData<SiteNavResult>(
    () => `navigation-${toValue(site)}-${toValue(locale)}`,
    async () => {
      const s = toValue(site)
      const l = toValue(locale)
      return _fetchNavData(s, l)
    },
    {
      // Refetch when site or locale changes — without `watch`, the reactive
      // key on its own doesn't re-trigger the handler, so the SubNavigationMenu
      // would render the previous locale's tabs after a language switch.
      watch: watchSources.length ? watchSources : undefined,
      // Nav structure is cached in dev — it changes far less often than page content
      // and re-fetching queryCollectionNavigation on every route change is expensive.
      // Hard-reload to pick up navigation changes (added/removed pages) during dev.
      ...persistentCache,
    },
  );

  // Keep the last resolved value so nav never flashes null during SPA transitions.
  // Uses a computed (not ref+watch) so it evaluates at render time during SSR,
  // picking up the resolved value after the caller awaits the composable.
  const rawData = result.data;
  let _lastResolved: SiteNavResult | null = null;
  const stableData = computed<SiteNavResult | null>(() => {
    const val = rawData.value;
    if (val != null) _lastResolved = val;
    return _lastResolved;
  });

  // Mutate `result` (don't spread) so the returned object keeps useAsyncData's
  // thenable identity — `{ ...result }` drops the non-enumerable `then`, making
  // `await useSiteNavigation()` resolve immediately, before the fetch lands.
  // Reactive consumers don't notice, but setup-time callers (the docs index
  // redirect) would read a null tree. `rawData` is captured above so the
  // computed doesn't reference its own replacement (infinite recursion).
  Object.assign(result, { data: stableData });
  return result;
}

export interface NavSectionChild {
  slug: string
  title: string
  description: string
  icon: string
  path: string
}

export interface NavColumn {
  key: string
  label?: string
  span?: number
  items: NavSectionChild[]
  indexPath?: string
}

export interface NavTab {
  key: string
  label: string
  icon?: string
  /** When set, the tab's rail entry navigates here on click (hover previews). */
  to?: string
  /** A tab always resolves to columns; a column-less tab becomes one flat column. */
  columns: NavColumn[]
}

export interface NavSection {
  slug: string
  title: string
  description: string
  icon: string
  image: string
  path: string
  hasIndex: boolean
  navigation: boolean
  children: NavSectionChild[]
  columns?: NavColumn[]
  /** Present when the section index declares `tabs:` — renders a tabbed panel. */
  tabs?: NavTab[]
}

/**
 * Returns pages from the unified `sections` collection scoped to one locale.
 *
 * The `sections` collection holds one row per nav-relevant page for every
 * locale (~hundreds of rows site-wide). Without a locale filter the entire
 * cross-locale set serializes into `__NUXT_DATA__` on every prerendered page
 * — ~150KB of payload per HTML page that the consumer immediately filters
 * back down. Filtering at the query level keeps the SSR payload small.
 *
 * Stems are `${locale}/${slug}/...`, so `stem LIKE '${locale}/%'` selects
 * exactly the rows for one locale.
 */
export function useSectionPages(locale: string | Ref<string>) {
  return useAsyncData(
    () => `section-pages-${toValue(locale)}`,
    async () => {
      const localeVal = toValue(locale)
      // Bounded on the Worker: the `sections` scan drives the section-tabs bar
      // and the mega-menu. If it exceeds budget on the Worker it degrades to an
      // empty set (no tabs / flat nav) rather than hanging the SSR render.
      // Prerender is unbounded, so baked pages always carry the full section set.
      return boundOnWorker(
        (await queryPages("sections"))
          .where("stem", "LIKE", `${localeVal}/%`)
          .select("id", "path", "stem", "title", "description", "icon", "image", "columns", "tabs", "nav_column", "nav_tab", "external_url", "navigation", "redirect")
          .all(),
        [] as any[],
      )
    },
    {
      // Refetch when locale changes — without `watch`, the reactive key alone
      // doesn't re-trigger the handler, so the nav renders the previous
      // locale's sections after a language switch.
      watch: isRef(locale) ? [locale] : undefined,
      ...persistentCache,
    },
  );
}

/**
 * Returns the top-level content sections for a given locale.
 * Every top-level content directory becomes a section. Sections with
 * children (sub-pages or sub-sites) get those as NavSectionChild entries.
 *
 * For docs, children come from stems like nl/docs/gebruiken/index → child = gebruiken.
 * For other dirs, children come from stems like nl/peppol/factuur → child = factuur.
 *
 * Cached per locale — multiple consumers share the same computed instance.
 */
const _sectionsCache = new Map<string, ComputedRef<NavSection[]>>()

export function useSections(locale: string | Ref<string>) {
  primeHiddenNavSet()
  const { data: allPages } = useSectionPages(locale)
  const archives = useArchives(locale)

  const localeVal = toValue(locale)
  const existing = _sectionsCache.get(localeVal)
  if (existing) return existing

  const stripPrefix = (slug: string) => slug.replace(/^\d+\./, '')
  const fallbackTitle = (slug: string) =>
    stripPrefix(slug).replace(/^./, (c) => c.toUpperCase())

  const result = computed<NavSection[]>(() => {
    const l = toValue(locale)
    const sectionMap = new Map<string, NavSection>()
    const declaredColumns = new Map<string, Array<{ key: string; label?: string; span?: number; link?: string }>>()
    const childColumnKey = new WeakMap<NavSectionChild, string>()
    type DeclaredTab = { key: string; label?: string; icon?: string; link?: string; columns?: Array<{ key: string; label?: string; span?: number; link?: string }> }
    const declaredTabs = new Map<string, DeclaredTab[]>()
    const childTabKey = new WeakMap<NavSectionChild, string>()

    for (const page of allPages.value ?? []) {
      const stem = (page.stem as string) ?? ''
      const parts = stem.split('/')
      if (parts.length < 3) continue
      const [partLocale, slug, partThird] = parts
      if (partLocale !== l || slug === undefined || partThird === undefined) continue

      // Section index: nl/{slug}/index
      if (parts.length === 3 && partThird === 'index') {
        if (!sectionMap.has(slug)) {
          sectionMap.set(slug, {
            slug,
            title: page.title ?? fallbackTitle(slug),
            description: page.description ?? '',
            icon: page.icon ?? '',
            image: page.image ?? '',
            path: page.path ?? `/${l}/${stripPrefix(slug)}`,
            hasIndex: true,
            navigation: page.navigation !== false,
            children: [],
          })
        } else {
          const existing = sectionMap.get(slug)!
          existing.title = page.title ?? fallbackTitle(slug)
          existing.description = page.description ?? ''
          existing.icon = page.icon ?? ''
          existing.image = page.image ?? ''
          existing.path = page.path ?? `/${l}/${stripPrefix(slug)}`
          existing.hasIndex = true
          existing.navigation = page.navigation !== false
        }
        const cols = page.columns as Array<{ key: string; label?: string; span?: number; link?: string }> | undefined
        if (cols?.length) declaredColumns.set(slug, cols)
        const tabs = page.tabs as DeclaredTab[] | undefined
        if (tabs?.length) declaredTabs.set(slug, tabs)
      }
      // Direct child: nl/{slug}/{child} (not index)
      else if (parts.length === 3 && partThird !== 'index') {
        if (!sectionMap.has(slug)) {
          sectionMap.set(slug, { slug, title: fallbackTitle(slug), description: '', icon: '', image: '', path: `/${l}/${stripPrefix(slug)}`, hasIndex: false, navigation: true, children: [] })
        }
        const directChildSlug = stripPrefix(partThird)
        // Dedupe — `useSectionPages` can return the same page row from
        // multiple stacked content collections (e.g. `sections` + a
        // commercial overlay), and pushing once per row would multiply the
        // entry on every recompute / SPA route change. Mirrors the same
        // guard the nested-child-index branch below already had.
        const section = sectionMap.get(slug)!
        if (section.children.some((c) => c.slug === directChildSlug)) continue
        // `navigation: false` opts a page out of nav. Honour it for children —
        // unless the page is explicitly placed in a mega-menu column via
        // `nav_column` (the redirect-entry pattern: hidden from the tree, shown
        // only as a column item).
        const key = page.nav_column as string | undefined
        const tabKey = page.nav_tab as string | undefined
        if (page.navigation === false && !key && !tabKey) continue
        const redirect = page.redirect as string | undefined
        const externalUrl = page.external_url as string | undefined
        const child: NavSectionChild = {
          slug: directChildSlug,
          title: page.title ?? directChildSlug,
          description: page.description ?? '',
          icon: page.icon ?? '',
          path: redirect ?? externalUrl ?? page.path ?? `/${l}/${stripPrefix(slug)}/${directChildSlug}`,
        }
        if (key) childColumnKey.set(child, key)
        if (tabKey) childTabKey.set(child, tabKey)
        section.children.push(child)
      }
      // Nested child index: nl/{slug}/{child}/index (e.g. docs sites)
      else if (parts.length === 4 && parts[3] === 'index') {
        if (!sectionMap.has(slug)) {
          sectionMap.set(slug, { slug, title: fallbackTitle(slug), description: '', icon: '', image: '', path: `/${l}/${stripPrefix(slug)}`, hasIndex: false, navigation: true, children: [] })
        }
        const childSlug = stripPrefix(partThird)
        // Only add if not already present (avoid duplicates from multiple locales)
        const section = sectionMap.get(slug)!
        // Honour `navigation: false` (e.g. the docs site-dir indexes
        // gebruiken/leren/bouwen) — skip unless explicitly placed in a column.
        const key = page.nav_column as string | undefined
        const tabKey = page.nav_tab as string | undefined
        if (page.navigation === false && !key && !tabKey) continue
        if (!section.children.some(c => c.slug === childSlug)) {
          const redirect = page.redirect as string | undefined
          const externalUrl = page.external_url as string | undefined
          const child: NavSectionChild = {
            slug: childSlug,
            title: page.title ?? childSlug,
            description: page.description ?? '',
            icon: page.icon ?? '',
            path: redirect ?? externalUrl ?? page.path ?? `/${l}/${stripPrefix(slug)}/${childSlug}`,
          }
          if (key) childColumnKey.set(child, key)
          if (tabKey) childTabKey.set(child, tabKey)
          section.children.push(child)
        }
      }
      // Deeper pages are ignored for nav purposes
    }

    // Drop children whose auth gate is off because the BFF is disabled (they
    // 404 server-side). Done before column/tab assembly so pruned children
    // never land in a mega-menu column. No-op set when the BFF is enabled.
    const hidden = getHiddenNavSet()
    if (hidden.size) {
      for (const section of sectionMap.values()) {
        section.children = section.children.filter((c) => !hidden.has(c.path))
      }
    }

    for (const section of sectionMap.values()) {
      // Tabbed panel: build tabs → columns → items. A tab with declared
      // `columns` fills them by `nav_tab` + `nav_column` (unassigned children
      // dropped, mirroring the column-drop rule); a column-less tab collapses to
      // one flat column over its `nav_tab` children. When a section declares both
      // `tabs` and `columns`, `tabs` wins (a panel is tabbed or flat, never both).
      const declaredT = declaredTabs.get(section.slug)
      if (declaredT?.length) {
        section.tabs = declaredT.map((tab) => {
          const cols = tab.columns ?? []
          const byKey = new Map<string, NavColumn>()
          for (const col of cols) byKey.set(col.key, { key: col.key, label: col.label, span: col.span, indexPath: col.link, items: [] })
          const flat: NavSectionChild[] = []
          for (const child of section.children) {
            if (childTabKey.get(child) !== tab.key) continue
            if (cols.length) {
              const colKey = childColumnKey.get(child)
              if (colKey && byKey.has(colKey)) byKey.get(colKey)!.items.push(child)
            } else {
              flat.push(child)
            }
          }
          const columns: NavColumn[] = cols.length
            ? cols.map((c) => byKey.get(c.key)!)
            : [{ key: '__flat__', label: '', span: 1, items: flat, indexPath: undefined }]
          return { key: tab.key, label: tab.label ?? tab.key, icon: tab.icon, to: tab.link, columns }
        })
        continue
      }
      const declared = declaredColumns.get(section.slug)
      if (!declared?.length) continue
      const byKey = new Map<string, NavColumn>()
      // `link` (authored per locale in the section index's `columns:`) becomes
      // the column's `indexPath` — MegaNavItem renders it as the column-header
      // "view all" link in the mega-menu panel.
      for (const col of declared) byKey.set(col.key, { key: col.key, label: col.label, span: col.span, indexPath: col.link, items: [] })
      // When a section declares columns, only children explicitly assigned to a
      // declared column (via `nav_column`) render — drop column-less / unknown-
      // column children instead of dumping them in a loose `__other` fallback.
      // Keeps the panel to exactly its declared columns, no stray items.
      for (const child of section.children) {
        const key = childColumnKey.get(child)
        if (!key || !byKey.has(key)) continue
        byKey.get(key)!.items.push(child)
      }
      section.columns = declared.map(c => byKey.get(c.key)!)
    }

    // Append `navigation: true` archives as top-level nav links (no children).
    for (const archive of archives.value ?? []) {
      if (!archive.navigation) continue
      if (sectionMap.has(archive.path)) continue
      sectionMap.set(archive.path, {
        slug: archive.path,
        title: archive.plural,
        description: '',
        icon: '',
        image: '',
        path: `/${l}/${archive.path}`,
        hasIndex: true,
        navigation: true,
        children: [],
      })
    }

    const sections = [...sectionMap.values()].filter((s) => !hidden.has(s.path))
    // Decode frontmatter entities (`&#8209;` etc.) once at the source so
    // every nav consumer renders the intended character via `{{ }}` without
    // needing a v-html escape hatch. Walks each section + its children and
    // mutates title/description in place.
    decodeNavTree(sections)
    for (const section of sections) {
      if (section.columns?.length) {
        for (const col of section.columns) {
          if (typeof col.label === "string") col.label = decodeEntities(col.label)
        }
      }
      if (section.tabs?.length) {
        for (const tab of section.tabs) {
          tab.label = decodeEntities(tab.label)
          for (const col of tab.columns) {
            if (typeof col.label === "string") col.label = decodeEntities(col.label)
          }
        }
      }
    }
    return sections
  })

  _sectionsCache.set(localeVal, result)
  return result
}

/**
 * Sections with `navigation: false` filtered out.
 * Use for rendering the navigation menu; use `useSections` when you need to
 * look up section metadata (e.g. localized title) regardless of nav visibility.
 */
export function useNavSections(locale: string | Ref<string>) {
  const all = useSections(locale)
  return computed<NavSection[]>(() => all.value.filter((s) => s.navigation))
}

export function useHomeLocales() {
  return useAsyncData(
    "home-locales",
    async () => (await queryPages("home")).select("id", "path", "stem", "title").all(),
    { ...persistentCache },
  );
}
